// ────────────────────────────────────────────────────────────
// TITLE-RUN PROGRESS STRIP
// ────────────────────────────────────────────────────────────
// Makes an in-flight title job visible in Deal Desk instead of the
// page looking stalled. Reads the title agent's run status (proxied
// via /api/agent/title-runs), polls every 15s, and renders a card per
// run: prominent stage headline (e.g. "fetch — 9/16 docs"), tract,
// live elapsed, doc count — failures loud and red. Same pattern as the
// MI skip-trace run panels.
//
//   scope 'all'  → the list page: every active or failed run.
//   scope 'deal' → the deal page: only the run for this deal's tract.

import { useEffect, useMemo, useState } from 'react';
import { agentApi, readTitleRuns, titleRunHeadline, type TitleRunView } from '../../mi/services/agentApi';

const POLL_MS = 15_000;

type Scope = { kind: 'all' } | { kind: 'deal'; trs: string; dealId?: string };

function elapsed(startIso: string | null, now: number): string | null {
  if (!startIso) return null;
  const t = Date.parse(startIso);
  if (!Number.isFinite(t)) return null;
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), rs = s % 60;
  if (m < 60) return `${m}m ${String(rs).padStart(2, '0')}s`;
  const h = Math.floor(m / 60), rm = m % 60;
  return `${h}h ${String(rm).padStart(2, '0')}m`;
}

export function TitleRunStrip({ scope }: { scope: Scope }) {
  const [runs, setRuns] = useState<TitleRunView[]>([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const raw = await agentApi.getTitleRuns();
        if (!cancelled) setRuns(readTitleRuns(raw));
      } catch { /* agent unreachable — keep the last snapshot, keep polling */ }
    };
    void poll();
    const t = setInterval(() => void poll(), POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Live elapsed ticker (only while something is active).
  const anyActive = runs.some(r => r.active);
  useEffect(() => {
    if (!anyActive) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anyActive]);

  const FAILED_WINDOW_MS = 24 * 60 * 60 * 1000;
  const visible = useMemo(() => {
    const trsUp = scope.kind === 'deal' ? scope.trs.toUpperCase() : null;
    const cutoff = Date.now() - FAILED_WINDOW_MS;
    return runs.filter(r => {
      // Show active runs always; failures only while recent (stale orphans
      // shouldn't linger forever). Successful/done runs surface as titled
      // deals, so they never show here.
      if (r.active) { /* keep */ }
      else if (r.failed) {
        const t = Date.parse(r.finishedAt ?? r.updatedAt ?? r.startedAt ?? '');
        if (Number.isFinite(t) && t < cutoff) return false;
      } else return false;
      if (scope.kind === 'all') return true;
      const matchTract = r.tract && trsUp && r.tract.toUpperCase().includes(trsUp);
      const matchDeal = scope.dealId && r.dealId === scope.dealId;
      return matchTract || matchDeal;
    });
  }, [runs, scope]);

  if (visible.length === 0) return null;

  return (
    <div className="dd-titleruns">
      {visible.map(run => {
        const cls = run.failed ? 'failed' : 'running';
        return (
          <div key={run.runId} className={`dd-titlerun ${cls}`}>
            <span className="dd-tr-dot" />
            <div className="dd-tr-main">
              <div className="dd-tr-head">
                <span className="dd-tr-headline">
                  {run.failed ? 'Title run failed' : `Title run · ${titleRunHeadline(run)}`}
                </span>
                {run.tract && <span className="dd-tr-tract">{run.tract}</span>}
                {!run.failed && <span className="dd-tr-elapsed">{elapsed(run.startedAt, now) ?? ''}</span>}
              </div>
              {run.failed
                ? <div className="dd-tr-error">{run.error ?? run.status ?? 'failed — check the agent log'}</div>
                : (run.detail && <div className="dd-tr-detail">{run.detail}</div>)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
